import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ArrowRight, AlertTriangle, Cog, Users, FileCheck, BarChart3, Clock, Shield, Upload, BookOpen, Award, Eye, CircleDot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const InstructionSystemPage = () => {
  const { language: lang } = useLanguage();

  const content = {
    de: {
      title: 'Digitales Unterweisungssystem',
      subtitle: 'Schulungspflichten einfach, effizient und dokumentiert erfüllen',
      backLink: 'Zurück zu Lösungen',
      
      intro: 'Unternehmen sind verpflichtet, ihre Mitarbeiter regelmäßig zu schulen – etwa in den Bereichen Arbeitssicherheit, IT-Security, Datenschutz oder Compliance. In der Praxis bedeutet dies jedoch oft einen hohen organisatorischen Aufwand: Schulungen müssen geplant, durchgeführt und dokumentiert werden. Mit dem e-Matrix Unterweisungssystem können Unternehmen diese Aufgaben effizient digitalisieren.',
      
      challenges: {
        title: 'Die Herausforderungen bei Pflichtschulungen',
        subtitle: 'Viele Unternehmen stehen bei Schulungen vor ähnlichen Problemen:',
        points: [
          'Hoher organisatorischer Aufwand',
          'Schwierige Terminplanung für Präsenzschulungen',
          'Fehlende Transparenz über den Schulungsstand',
          'Hoher Zeitaufwand für Dokumentation',
          'Schwierige Nachweisführung bei Audits'
        ],
        conclusion: 'Gerade bei Unternehmen mit vielen Mitarbeitern oder mehreren Standorten wird die Organisation von Schulungen schnell sehr aufwendig.'
      },
      
      solution: {
        title: 'Die Lösung: Das e-Matrix Unterweisungssystem',
        subtitle: 'Das e-Matrix Unterweisungssystem digitalisiert den gesamten Schulungsprozess.',
        points: [
          'Schulungen werden zentral verwaltet, digital durchgeführt und automatisch dokumentiert.',
          'Mitarbeiter können ihre Schulungen zeit- und ortsunabhängig absolvieren.',
          'Verantwortliche behalten jederzeit den Überblick über alle Schulungsstände.'
        ],
        conclusion: 'Dadurch wird der organisatorische Aufwand deutlich reduziert und gleichzeitig eine vollständige Dokumentation sichergestellt.'
      },
      
      howItWorks: {
        title: 'So funktioniert das System',
        steps: [
          { icon: 'upload', text: 'Mitarbeiter werden im System angelegt (z. B. über Excel-Import oder HR-Schnittstellen).' },
          { icon: 'assign', text: 'Die passenden Schulungen werden automatisch zugewiesen.' },
          { icon: 'learn', text: 'Mitarbeiter absolvieren die Schulungen online.' },
          { icon: 'track', text: 'Das System dokumentiert automatisch den Schulungsfortschritt.' },
          { icon: 'cert', text: 'Zertifikate werden automatisch erstellt und gespeichert.' },
          { icon: 'overview', text: 'Verantwortliche sehen jederzeit den aktuellen Schulungsstand.' }
        ]
      },
      
      benefits: {
        title: 'Ihre Vorteile',
        items: [
          {
            title: 'Weniger organisatorischer Aufwand',
            description: 'Schulungen müssen nicht mehr manuell geplant und organisiert werden.'
          },
          {
            title: 'Zeit- und Kostenersparnis',
            description: 'Digitale Schulungen reduzieren Reisezeiten, Trainerkosten und Organisationsaufwand.'
          },
          {
            title: 'Transparenter Überblick',
            description: 'Verantwortliche sehen jederzeit: welche Mitarbeiter geschult wurden, welche Schulungen noch offen sind, welche Fristen anstehen.'
          },
          {
            title: 'Automatische Dokumentation',
            description: 'Alle Schulungen werden automatisch dokumentiert und sind jederzeit nachweisbar.'
          },
          {
            title: 'Audit- und Compliance-Sicherheit',
            description: 'Alle Schulungsnachweise stehen jederzeit für interne oder externe Audits zur Verfügung.'
          }
        ]
      },
      
      cta: {
        title: 'Interesse an unserem Unterweisungssystem?',
        text: 'Kontaktieren Sie uns für eine individuelle Beratung und ein unverbindliches Angebot.',
        button: 'Kontakt aufnehmen'
      }
    },
    en: {
      title: 'Digital Instruction System',
      subtitle: 'Fulfill training obligations simply, efficiently and documented',
      backLink: 'Back to Solutions',
      
      intro: 'Companies are required to regularly train their employees – for example in the areas of occupational safety, IT security, data protection or compliance. In practice, however, this often means a lot of organizational effort: training must be planned, conducted and documented. With the e-Matrix instruction system, companies can efficiently digitize these tasks.',
      
      challenges: {
        title: 'The Challenges of Mandatory Training',
        subtitle: 'Many companies face similar problems with training:',
        points: [
          'High organizational effort',
          'Difficult scheduling for in-person training',
          'Lack of transparency about training status',
          'High time expenditure for documentation',
          'Difficult proof of compliance for audits'
        ],
        conclusion: 'Especially for companies with many employees or multiple locations, organizing training quickly becomes very complex.'
      },
      
      solution: {
        title: 'The Solution: The e-Matrix Instruction System',
        subtitle: 'The e-Matrix instruction system digitizes the entire training process.',
        points: [
          'Training is centrally managed, digitally conducted and automatically documented.',
          'Employees can complete their training regardless of time and location.',
          'Managers always have an overview of all training statuses.'
        ],
        conclusion: 'This significantly reduces organizational effort while ensuring complete documentation.'
      },
      
      howItWorks: {
        title: 'How the System Works',
        steps: [
          { icon: 'upload', text: 'Employees are added to the system (e.g., via Excel import or HR interfaces).' },
          { icon: 'assign', text: 'Appropriate training is automatically assigned.' },
          { icon: 'learn', text: 'Employees complete training online.' },
          { icon: 'track', text: 'The system automatically documents training progress.' },
          { icon: 'cert', text: 'Certificates are automatically created and stored.' },
          { icon: 'overview', text: 'Managers can view current training status at any time.' }
        ]
      },
      
      benefits: {
        title: 'Your Benefits',
        items: [
          {
            title: 'Less Organizational Effort',
            description: 'Training no longer needs to be manually planned and organized.'
          },
          {
            title: 'Time and Cost Savings',
            description: 'Digital training reduces travel time, trainer costs and organizational effort.'
          },
          {
            title: 'Transparent Overview',
            description: 'Managers can see at any time: which employees have been trained, which training is still pending, which deadlines are coming up.'
          },
          {
            title: 'Automatic Documentation',
            description: 'All training is automatically documented and can be verified at any time.'
          },
          {
            title: 'Audit and Compliance Security',
            description: 'All training records are available at any time for internal or external audits.'
          }
        ]
      },
      
      cta: {
        title: 'Interested in our Instruction System?',
        text: 'Contact us for individual consultation and a non-binding offer.',
        button: 'Contact Us'
      }
    }
  };

  const t = content[lang];

  const seoContent = {
    de: {
      title: 'Digitales Unterweisungssystem - E-Learning Schulungsmanagement',
      description: 'e-Matrix Unterweisungssystem: Pflichtschulungen digital verwalten, durchführen und dokumentieren. Arbeitssicherheit, Compliance, DSGVO. SCORM-kompatibel. Automatische Zertifikate.',
      keywords: 'Unterweisungssystem, digitale Unterweisung, Pflichtschulung online, E-Learning Schulungsmanagement, Arbeitssicherheit Unterweisung, Compliance Schulung, SCORM LMS, Mitarbeiterschulung digital, ASchG Unterweisung, Schulungsdokumentation, ematrixsys'
    },
    en: {
      title: 'Digital Training System - E-Learning Training Management',
      description: 'e-Matrix Training System: Manage, deliver and document mandatory training digitally. Workplace safety, compliance, GDPR. SCORM-compatible. Automatic certificates.',
      keywords: 'Training System, digital instruction, mandatory training online, E-Learning training management, workplace safety training, compliance training, SCORM LMS, digital employee training, training documentation, ematrixsys'
    }
  };

  const seo = seoContent[lang] || seoContent.de;

  const getStepIcon = (iconName) => {
    const icons = {
      upload: <Upload className="w-6 h-6" />,
      assign: <Users className="w-6 h-6" />,
      learn: <BookOpen className="w-6 h-6" />,
      track: <BarChart3 className="w-6 h-6" />,
      cert: <Award className="w-6 h-6" />,
      overview: <Eye className="w-6 h-6" />
    };
    return icons[iconName] || <Cog className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E7F0FA] to-white pt-20">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path="/instruction-system"
        lang={lang}
      />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#C8D8E8]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link 
            to="/solutions" 
            className="inline-flex items-center gap-2 text-[#2E5E99] hover:text-[#1E4F85] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backLink}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
            {t.title}
          </h1>
          <p className="text-[#5A7A9B] mt-2 text-lg">
            {t.subtitle}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero Image */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/instruction-system.jpeg" 
              alt={t.title}
              className="w-full h-[400px] object-cover object-[75%_30%]"
            />
          </div>
        </motion.section>

        {/* Introduction */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <p className="text-[#5A7A9B] text-lg leading-relaxed">
              {t.intro}
            </p>
          </div>
        </motion.section>

        {/* Challenges */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="bg-amber-50 rounded-xl p-6 md:p-8 border border-amber-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#0D2440]">{t.challenges.title}</h2>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.challenges.subtitle}</p>
            <ul className="space-y-2 mb-4">
              {t.challenges.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CircleDot className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] italic">{t.challenges.conclusion}</p>
          </div>
        </motion.section>

        {/* Solution */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <div className="bg-gradient-to-br from-[#E7F0FA] to-white rounded-xl p-6 md:p-8 border border-[#2E5E99]/30 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#2E5E99]/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#2E5E99]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0D2440]">{t.solution.title}</h2>
            </div>
            <p className="text-[#0D2440] font-medium mb-4">{t.solution.subtitle}</p>
            <ul className="space-y-3 mb-4">
              {t.solution.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#2E5E99] font-medium">{t.solution.conclusion}</p>
          </div>
        </motion.section>

        {/* How It Works */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-[#0D2440] text-center mb-8">{t.howItWorks.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.howItWorks.steps.map((step, i) => (
              <div key={step.text} className="bg-white rounded-xl p-6 border border-[#C8D8E8] shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 bg-[#2E5E99] text-white rounded-full flex items-center justify-center font-bold">
                    {i + 1}
                  </div>
                  <div className="w-10 h-10 bg-[#E7F0FA] rounded-lg flex items-center justify-center text-[#2E5E99]">
                    {getStepIcon(step.icon)}
                  </div>
                </div>
                <p className="text-[#5A7A9B]">{step.text}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-[#0D2440] text-center mb-8">{t.benefits.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.benefits.items.map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-6 border border-[#C8D8E8] shadow-lg">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#0D2440] mb-2">{item.title}</h3>
                <p className="text-[#5A7A9B] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="bg-gradient-to-r from-[#2E5E99] to-[#1E4F85] rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {t.cta.title}
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              {t.cta.text}
            </p>
            <Link to="/booking">
              <Button className="bg-white text-[#2E5E99] hover:bg-white/90 px-8 py-3 h-auto text-base">
                {t.cta.button}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default InstructionSystemPage;
